import { Link } from "react-router";
import { Mail } from "lucide-react";
import { NewsletterForm } from "../components/NewsletterForm";

const COLS: Array<{ title: string; links: Array<{ label: string; to?: string; href?: string }> }> = [
  {
    title: "Platform",
    links: [
      { label: "Overview", to: "/platform" },
      { label: "Features", to: "/features" },
      { label: "Pricing", to: "/pricing" },
      { label: "Customers", to: "/customers" },
      { label: "Request demo", to: "/demo" },
    ],
  },
  {
    title: "Solutions",
    links: [
      { label: "Long-term care (SNF)", to: "/solutions/snf" },
      { label: "Ambulatory surgical (ASC)", to: "/solutions/asc" },
      { label: "Dialysis", to: "/solutions/dialysis" },
    ],
  },
  {
    title: "Company",
    links: [
      { label: "About", to: "/about" },
      { label: "Careers", to: "/careers" },
      { label: "Press kit", to: "/press" },
      { label: "Contact", to: "/contact" },
    ],
  },
  {
    title: "Resources",
    links: [
      { label: "Resource hub", to: "/resources" },
      { label: "Blog", to: "/blog" },
      { label: "Docs", to: "/docs" },
      { label: "Sitemap", to: "/sitemap" },
    ],
  },
  {
    title: "Trust",
    links: [
      { label: "Trust center", to: "/trust" },
      { label: "Security", to: "/security" },
      { label: "HIPAA", to: "/hipaa" },
      { label: "Privacy", to: "/privacy" },
      { label: "Terms", to: "/terms" },
      { label: "DPA", to: "/dpa" },
      { label: "BAA", to: "/baa" },
      { label: "SLA", to: "/sla" },
      { label: "Subprocessors", to: "/subprocessors" },
      { label: "Status", to: "/status" },
    ],
  },
];

export function Footer() {
  return (
    <footer className="bg-ink-900 text-ink-200 mt-24">
      <div className="mx-auto max-w-container px-6 md:px-8 py-16">
        <div className="grid gap-12 lg:grid-cols-[1.4fr_3fr]">
          {/* Brand block */}
          <div>
            <Link to="/" aria-label="WeConsult Rx home" className="inline-flex">
              <img
                src="/logo-stacked-on-dark.svg"
                alt="WeConsult Rx"
                width="220"
                height="154"
                className="h-32 w-auto"
              />
            </Link>
            <p className="mt-4 text-sm text-ink-300 max-w-xs">
              Modern consultant pharmacy operations — clinical engine, compliance
              automation, and AI-augmented workflow for SNF, ASC, and dialysis.
            </p>
            <div className="mt-6">
              <p className="text-xs uppercase tracking-tagline font-semibold text-forest-400">
                Newsletter
              </p>
              <p className="mt-2 text-sm text-ink-300">
                Quarterly product updates. No spam.
              </p>
              <NewsletterForm className="mt-3" />
            </div>
          </div>

          {/* Link columns */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {COLS.map((col) => (
              <div key={col.title}>
                <p className="text-xs uppercase tracking-tagline font-semibold text-forest-400">
                  {col.title}
                </p>
                <ul className="mt-3 space-y-2">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.to ? (
                        <Link to={l.to} className="text-sm text-ink-300 hover:text-white">
                          {l.label}
                        </Link>
                      ) : (
                        <a
                          href={l.href}
                          className="text-sm text-ink-300 hover:text-white"
                        >
                          {l.label}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-ink-800 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <p className="text-xs text-ink-400">
            &copy; {new Date().getFullYear()} WeConsult Rx, a DRX Consulting Group company. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-ink-400">
            <a className="inline-flex items-center gap-1 hover:text-white" href="mailto:hello@weconsultrx.com">
              <Mail className="h-3.5 w-3.5" /> hello@weconsultrx.com
            </a>
            <Link to="/accessibility" className="hover:text-white">
              Accessibility
            </Link>
            <Link to="/cookies" className="hover:text-white">
              Cookies
            </Link>
            <Link to="/gdpr" className="hover:text-white">
              GDPR
            </Link>
            <Link to="/ccpa" className="hover:text-white">
              CCPA
            </Link>
            <Link to="/responsible-disclosure" className="hover:text-white">
              Disclosure
            </Link>
            <Link to="/sms-terms" className="hover:text-white">
              SMS Terms
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
